import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Menu, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { useI18n } from '@/context/LanguageContext';
import { Logo } from '@/components/Logo';
import { scrollToId } from '@/lib/scroll';

const SECTIONS = [
  { id: 'inicio', key: 'nav.home' },
  { id: 'metodologia', key: 'nav.methodology' },
  { id: 'datos', key: 'nav.data' },
  { id: 'transparencia', key: 'nav.transparency' },
  { id: 'contacto', key: 'nav.contact' },
];

export function Header() {
  const { t, lang } = useI18n();
  const { lang: routeLang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isHome = location.pathname === `/${lang}` || location.pathname === `/${lang}/`;
  const otherLang = lang === 'ar' ? 'en' : 'ar';

  const goToSection = (id) => {
    if (isHome) {
      scrollToId(id);
    } else {
      navigate(`/${lang}#${id}`);
    }
  };

  const handleNav = (id) => (e) => {
    e.preventDefault();
    setOpen(false);
    goToSection(id);
  };

  const switchLanguage = () => {
    const rest = location.pathname.replace(/^\/(ar|en)(?=\/|$)/, '');
    navigate(`/${otherLang}${rest}${location.hash || ''}`);
    setOpen(false);
  };

  return (
    <header className="inv-header sticky top-0 z-50 border-b border-border/70 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70">
      <div className="mx-auto flex h-16 max-w-[1120px] items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to={`/${lang}`}
          data-testid="header-logo-link"
          className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
          onClick={() => isHome && window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          <Logo />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`/${lang}#${s.id}`}
              onClick={handleNav(s.id)}
              data-testid={`header-nav-${s.id}`}
              className="inv-navlink font-heading text-sm text-[hsl(var(--inv-slate))] hover:text-[hsl(var(--inv-blue))]"
            >
              {t(s.key)}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={switchLanguage}
            data-testid="header-language-switcher"
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 px-2.5 py-1.5 text-sm font-medium text-[hsl(var(--inv-slate))] transition-colors hover:bg-[hsl(var(--muted))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]"
            aria-label={t('common.otherLangName')}
          >
            <Globe className="h-4 w-4" />
            <span className="font-mono uppercase">{otherLang}</span>
          </button>

          <Button
            onClick={() => goToSection('contacto')}
            data-testid="header-request-access-desktop"
            className="hidden bg-[hsl(var(--inv-blue))] text-[hsl(var(--primary-foreground))] transition-colors hover:bg-[hsl(var(--inv-blue-2))] active:scale-[0.99] lg:inline-flex"
          >
            {t('nav.requestAccess')}
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                data-testid="header-mobile-menu-button"
                aria-label="Menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86vw] max-w-sm">
              <SheetHeader>
                <SheetTitle className="text-left">
                  <Logo />
                </SheetTitle>
                <SheetDescription className="sr-only">Invelitio</SheetDescription>
              </SheetHeader>
              <div className="mt-6 flex flex-col">
                {SECTIONS.map((s) => (
                  <a
                    key={s.id}
                    href={`/${lang}#${s.id}`}
                    onClick={handleNav(s.id)}
                    data-testid={`header-mobile-nav-${s.id}`}
                    className="rounded-md px-2 py-3 font-heading text-base text-[hsl(var(--inv-slate))] transition-colors hover:bg-[hsl(var(--muted))]"
                  >
                    {t(s.key)}
                  </a>
                ))}
                <Separator className="my-4" />
                <Button
                  onClick={() => goToSection('contacto')}
                  data-testid="header-request-access-mobile"
                  className="w-full bg-[hsl(var(--inv-blue))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--inv-blue-2))]"
                >
                  {t('nav.requestAccess')}
                </Button>
                <SheetClose asChild>
                  <button
                    type="button"
                    onClick={switchLanguage}
                    className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-md border border-border/70 px-3 py-2 text-sm font-medium transition-colors hover:bg-[hsl(var(--muted))]"
                  >
                    <Globe className="h-4 w-4" />
                    {t('common.otherLangName')}
                  </button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export default Header;
